/** @graphql cc **/
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager';

import type { ProgressiveRenderingConfigQuery as ProgressiveRenderingConfigQueryType } from './__types__/ProgressiveRenderingConfigQuery';
import type { AdfStats } from './useAdfStats';

const STATSIG_DYNAMIC_CONFIG = 'cc_complexit_page_id_allowlist';

type UseProgressiveRenderingConfigResponseType = {
	isComplexPage: boolean;
	batchSize: number;
};

type UseProgressiveRenderingConfigProps = {
	skip?: boolean;
	totalNodeCount: AdfStats['totalNodeCount'];
};

const DEFAULT_BATCH_SIZE = 1000;
const DEFAULT_MULTIPLIER = 10;

// complexitNodes value example: conplexitNodes=1000,10 where 1000 is the batch size and 10 is the multiplier
const useQueryParamComplexitNodes = createSingleQueryParamHook('complexitNodes');

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
const ProgressiveRenderingConfigQuery = gql`
	query ProgressiveRenderingConfigQuery {
		user: confluence_atlassianUser(current: true) {
			id
			emails {
				value
			}
		}
	}
`;

export const useProgressiveRenderingConfig = ({
	skip,
	totalNodeCount,
}: UseProgressiveRenderingConfigProps): UseProgressiveRenderingConfigResponseType => {
	const complexitNodesConfig = useQueryParamComplexitNodes()?.split(',');

	const { data } = useQuery<ProgressiveRenderingConfigQueryType>(ProgressiveRenderingConfigQuery, {
		skip:
			skip ||
			!fg('cc_complexit_fe_progressive_adf_rendering_v2') ||
			!complexitNodesConfig ||
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR),
	});

	const [contentId] = usePageContentId();

	let isComplexPage = false;
	let batchSize = DEFAULT_BATCH_SIZE;

	if (!skip) {
		let multiplier = DEFAULT_MULTIPLIER;

		// Allow Atlassian employees to override the default config:
		const isAtlassianUser = data?.user?.emails?.some((email) =>
			email?.value?.endsWith('@atlassian.com'),
		);
		if (isAtlassianUser) {
			// Support partial value format like ,10 or 1000,
			const complexitNodesBatchSize = Number(complexitNodesConfig?.[0]);
			if (complexitNodesBatchSize > 0) {
				batchSize = complexitNodesBatchSize;
			}
			const complexitNodesMultiplier = Number(complexitNodesConfig?.[1]);
			if (complexitNodesMultiplier > 0) {
				multiplier = complexitNodesMultiplier;
			}
		}

		// Figure out whether progressive rendering is to be enabled:
		if (fg('cc_complexit_fe_progressive_rendering_condition')) {
			if (totalNodeCount >= batchSize * multiplier) {
				isComplexPage = true;
			} else if (contentId) {
				const pageIdAllowList = StatsigConfigurations.getStringList(STATSIG_DYNAMIC_CONFIG, []);
				isComplexPage = pageIdAllowList.includes(contentId);
			}
		}
	}

	return {
		isComplexPage,
		batchSize,
	};
};
